import { BaseApiService } from './base.service';

export interface FieldChange {
  field_name: string;
  old_value: any;
  new_value: any;
  category: string;
}

export interface ChangeLogResponse {
  id: number;
  entity_type: string;
  entity_id: number;
  original_entity_id: number;
  server_id: number;
  change_type: string;
  field_changes: Record<string, any>;
  categorized_changes: FieldChange[];
  contract_id?: string;
  contract_name?: string;
  entity_name?: string;
  supervisor_name?: string;
  is_reviewed: boolean;
  is_applied: boolean;
  reviewed_by?: string;
  reviewed_at?: string;
  review_action?: string;
  review_notes?: string;
  import_source?: string;
  import_timestamp: string;
  created_at: string;
  updated_at?: string;
  auto_approvable: boolean;
}

export interface ReviewStats {
  total_pending: number;
  total_accepted: number;
  total_rejected: number;
  auto_approvable: number;
  by_entity_type: Record<string, number>;
  by_category: Record<string, number>;
  by_server: Record<string, number>;
}

export interface ReviewChangeRequest {
  action: 'accept' | 'reject' | 'partial';
  notes?: string;
  reviewed_by: string;
}

export interface ReviewChangeResponse {
  success: boolean;
  message: string;
  change_id: number;
  action: string;
  applied: boolean;
}

export interface BulkReviewRequest {
  change_ids: number[];
  action: 'accept' | 'reject' | 'partial';
  notes?: string;
  reviewed_by: string;
}

export interface BulkReviewResponse {
  success: boolean;
  message: string;
  processed_count: number;
  successful_count: number;
  failed_count: number;
  results: ReviewChangeResponse[];
  errors?: string[];
}

export interface ReviewFilters {
  server_id?: number;
  entity_type?: string;
  change_type?: string;
  supervisor_name?: string;
  review_status?: string;
  category?: string;
}

export interface AutoApprovalResult {
  success: boolean;
  message: string;
  auto_approved_count: number;
  auto_approved_changes: number[];
}

/**
 * Service for review automation operations
 */
export class ReviewAutomationService extends BaseApiService {
  private readonly basePath = '/api/v1/review-automation';

  async getChanges(
    filters: ReviewFilters = {},
    skip: number = 0,
    limit: number = 100
  ): Promise<ChangeLogResponse[]> {
    const params = new URLSearchParams();
    
    if (filters.server_id) params.append('server_id', filters.server_id.toString());
    if (filters.entity_type) params.append('entity_type', filters.entity_type);
    if (filters.change_type) params.append('change_type', filters.change_type);
    if (filters.supervisor_name) params.append('supervisor_name', filters.supervisor_name);
    if (filters.review_status) params.append('review_status', filters.review_status);
    if (filters.category) params.append('category', filters.category);
    
    params.append('skip', skip.toString());
    params.append('limit', limit.toString());

    return this.get(`${this.basePath}/changes?${params.toString()}`);
  }

  async getReviewStats(serverId?: number): Promise<ReviewStats> {
    const params = serverId ? `?server_id=${serverId}` : '';
    return this.get(`${this.basePath}/changes/stats${params}`);
  }

  async reviewChange(
    changeId: number,
    request: ReviewChangeRequest
  ): Promise<ReviewChangeResponse> {
    return this.post(`${this.basePath}/changes/${changeId}/review`, request);
  }

  async bulkReviewChanges(request: BulkReviewRequest): Promise<BulkReviewResponse> {
    return this.post(`${this.basePath}/changes/bulk-review`, request);
  }

  async autoApproveTimezoneChanges(serverId?: number): Promise<AutoApprovalResult> {
    const params = serverId ? `?server_id=${serverId}` : '';
    return this.post(`${this.basePath}/changes/auto-approve${params}`);
  }

  async getChangeCategories(serverId?: number): Promise<string[]> {
    const params = serverId ? `?server_id=${serverId}` : '';
    return this.get(`${this.basePath}/changes/categories${params}`);
  }

  async getChangeDetails(changeId: number): Promise<ChangeLogResponse> {
    return this.get(`${this.basePath}/changes/${changeId}`);
  }

  async deleteChange(changeId: number): Promise<{ success: boolean; message: string }> {
    return this.delete(`${this.basePath}/changes/${changeId}`);
  }

  async exportChanges(filters: ReviewFilters = {}): Promise<Blob> {
    const params = new URLSearchParams();
    
    if (filters.server_id) params.append('server_id', filters.server_id.toString());
    if (filters.entity_type) params.append('entity_type', filters.entity_type);
    if (filters.change_type) params.append('change_type', filters.change_type);
    if (filters.review_status) params.append('review_status', filters.review_status);

    const response = await this.axiosInstance.get(
      `${this.basePath}/changes/export?${params.toString()}`,
      { responseType: 'blob' }
    );
    
    return response.data;
  }
}

export const reviewAutomationService = new ReviewAutomationService();